import { createSlice, type PayloadAction, current } from "@reduxjs/toolkit";
import type { CreateJobRoleResponse } from "../features/dashBoard/types";
import type { JobFormData, JobRoleState } from "./types";

const getInitialState = (): JobRoleState => {
  const defaultState: JobRoleState = {
    generatedJD: null,
    formData: null,
    uploadedFile: null,
    extractionId: null,
    cloneJobId: null,
  };
  try {
    const saved = sessionStorage.getItem("create_job_role_state");
    return saved ? JSON.parse(saved) : defaultState;
  } catch (error) {
    console.error("Failed to parse create_job_role_state from sessionStorage", error);
    return defaultState;
  }
};

const initialState: JobRoleState = getInitialState();

const save = (state: JobRoleState) => {
  try {
    sessionStorage.setItem(
      "create_job_role_state",
      JSON.stringify({ ...current(state), uploadedFile: null }),
    );
  } catch (error) {
    console.error("Failed to save create_job_role_state to sessionStorage", error);
  }
};

const createJobRoleSlice = createSlice({
  name: "jobRole",
  initialState,
  reducers: {
    /* CREATE (AI) */
    setGeneratedJD: (state, action: PayloadAction<CreateJobRoleResponse>) => {
      const job = action.payload.data;

      state.generatedJD = job.job_description ?? "";
      state.formData = {
        jobTitle: job.job_title ?? "",
        description: job.job_description ?? "",
        location: job.location ?? "",
        minimumExperience: job.minimum_experience ?? null,
        maximumExperience: job.maximum_experience ?? null,
        skills: job.skills ?? [],
        ask_dsa: job.ask_dsa ?? false,
        ask_coding: job.ask_coding ?? false,
        ask_cultural: job.ask_cultural ?? false,
        ask_soft_skill: job.ask_soft_skill ?? false,
        ask_system_design: job.ask_system_design ?? false,
        extractionId: job.job_extraction_id,
      };
      save(state);
    },

    setCloneJobId: (state, action: PayloadAction<string | null>) => {
      state.cloneJobId = action.payload;
      save(state);
    },

    /* STORE FILE */
    setUploadedFile: (state, action: PayloadAction<File | null>) => {
      state.uploadedFile = action.payload;
    },

    /* EDIT (API) */
    setFormDataFromJob: (state, action: PayloadAction<JobFormData>) => {
      state.formData = action.payload;
      save(state);
    },

    clearGeneratedJD: (state) => {
      state.generatedJD = null;
      state.formData = null;
      state.uploadedFile = null;
      state.extractionId = null;
      state.cloneJobId = null;
      sessionStorage.removeItem("create_job_role_state");
    },
  },
});

export const {
  setGeneratedJD,
  setFormDataFromJob,
  clearGeneratedJD,
  setUploadedFile,
  setCloneJobId,
} = createJobRoleSlice.actions;

export default createJobRoleSlice.reducer;
